import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Loader2, TrendingDown, TrendingUp, Minus, Info, ArrowRight, ExternalLink, ChevronDown } from "lucide-react";
import { fetchDraws } from "@/lib/api";
import { PROVINCES, PROVINCE_COLORS } from "@/lib/constants";
import { DrawPageAlert } from "@/components/DrawPageAlert";

export const fmtDrawDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });

export function Trend({ rounds }) {
  const [a, b] = rounds.filter((r) => r.cutoff != null);
  if (!a || !b || a.cutoff == null || b.cutoff == null) return <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Minus className="h-3.5 w-3.5" /> n/a</span>;
  const d = a.cutoff - b.cutoff;
  if (d === 0) return <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500"><Minus className="h-3.5 w-3.5" /> unchanged</span>;
  const up = d > 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${up ? "text-[#C8102E]" : "text-emerald-600"}`} data-testid="draw-trend">
      {up ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />} {up ? "+" : ""}{d} vs previous
    </span>
  );
}

export default function Draws() {
  const [linksOpen, setLinksOpen] = useState(() => typeof window !== "undefined" && window.innerWidth >= 768);
  const [data, setData] = useState(null);
  const [province, setProvince] = useState("ALL");
  useEffect(() => { fetchDraws().then(setData).catch(() => setData({ streams: [], note: "" })); }, []);
  const items = useMemo(() => (data?.streams || []).filter((s) => province === "ALL" || s.province === province), [data, province]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="draws-page">
      <span className="inline-flex items-center gap-2 rounded-full bg-[#FEF2F2] px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#C8102E]"><CalendarDays className="h-3.5 w-3.5" /> Draw history</span>
      <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Recent invitation rounds</h1>
      <p className="mt-2 max-w-2xl text-slate-600">Track when each stream invites and at what score, so you can time your Expression of Interest.</p>
      {data?.note && <p className="mt-4 inline-flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="draws-note"><Info className="mt-0.5 h-4 w-4 shrink-0" /> {data.note}</p>}

      {data?.links && (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5" data-testid="official-draw-links">
          <button onClick={() => setLinksOpen((o) => !o)} className="flex w-full items-center justify-between gap-3 text-left md:pointer-events-none" data-testid="official-links-toggle" aria-expanded={linksOpen}>
            <div>
              <h2 className="font-heading text-base font-semibold text-slate-900">Official draw pages</h2>
              <p className="text-xs text-slate-500">Always confirm the latest rounds at the source — these are the provinces' own invitation/draw pages.</p>
            </div>
            <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform md:hidden ${linksOpen ? "rotate-180" : ""}`} />
          </button>
          <ul className={`mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4 ${linksOpen ? "" : "hidden md:grid"}`}>
            {PROVINCES.filter((p) => data.links[p.code]).map((p) => (
              <li key={p.code}>
                <a href={data.links[p.code].url} target="_blank" rel="noreferrer" data-testid={`draw-link-${p.code}`}
                  className="flex items-start gap-2 rounded-lg border border-slate-200 px-3 py-2.5 text-sm transition-colors hover:border-[#C8102E] hover:bg-[#FEF2F2]">
                  <span className="mt-0.5 rounded px-1.5 py-0.5 text-xs font-bold text-white" style={{ backgroundColor: PROVINCE_COLORS[p.code] }}>{p.code}</span>
                  <span className="flex-1 text-slate-700">{data.links[p.code].label}</span>
                  <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        {[{ code: "ALL", name: "All provinces" }, ...PROVINCES].map((p) => (
          <button key={p.code} onClick={() => setProvince(p.code)} data-testid={`draws-province-${p.code}`}
            className={`min-h-[40px] rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${province === p.code ? "border-[#0F172A] bg-[#0F172A] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>{p.name}</button>
        ))}
      </div>

      {!data ? <div className="mt-16 flex justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div> : items.length === 0 ? (
        <p className="mt-10 text-sm text-slate-500" data-testid="draws-empty">No published rounds for this province yet.</p>
      ) : (
        <div className="mt-6 grid gap-5 md:grid-cols-2" data-testid="draws-grid">
          {items.map((s) => <DrawCard key={s.id} s={s} />)}
        </div>
      )}
    </div>
  );
}

function DrawCard({ s }) {
  const latest = s.rounds[0];
  const latestScored = s.rounds.find((r) => r.cutoff != null);
  const unit = s.scoring_model === "crs_based" ? "CRS" : s.scoring_model === "grid" ? `/ ${s.max_points}` : "";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`draw-card-${s.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-bold text-white" style={{ backgroundColor: PROVINCE_COLORS[s.province] }}>{s.province}</span>
          <h3 className="mt-2 font-heading text-lg font-semibold text-slate-900">{s.name}</h3>
          <p className="text-xs text-slate-500">{s.stream_group}</p>
        </div>
        <div className="shrink-0 min-w-[8rem] text-right">
          <p className="text-xs uppercase tracking-wider text-slate-400">Latest cutoff</p>
          <p className="font-mono-tech text-3xl font-bold text-[#0F172A]" data-testid={`draw-latest-${s.id}`}>{latestScored?.cutoff ?? "—"}</p>
          <p className="text-xs text-slate-400">{unit}</p>
          <Trend rounds={s.rounds} />
        </div>
      </div>
      <table className="mt-4 w-full text-sm">
        <thead className="text-left text-xs font-semibold uppercase tracking-wider text-slate-400"><tr><th className="py-1.5">Date</th><th className="py-1.5">Cutoff</th><th className="py-1.5">Invites</th><th className="py-1.5">Round</th></tr></thead>
        <tbody className="divide-y divide-slate-100">
          {s.rounds.map((r) => (
            <tr key={r.date + r.note}><td className="py-2 text-slate-700">{fmtDrawDate(r.date)}</td><td className="py-2 font-mono-tech font-semibold text-slate-900">{r.cutoff ?? "—"}</td><td className="py-2 text-slate-600">{r.invitations ?? "—"}</td><td className="py-2 text-slate-500">{r.note}</td></tr>
          ))}
        </tbody>
      </table>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1">
        <Link to={`/assessment?stream=${s.id}`} data-testid={`draw-calc-${s.id}`} className="inline-flex min-h-[44px] items-center gap-1 text-sm font-semibold text-[#C8102E] hover:underline">Calculate my score for this stream <ArrowRight className="h-4 w-4" /></Link>
        {s.draw_link && <a href={s.draw_link.url} target="_blank" rel="noreferrer" data-testid={`draw-official-${s.id}`} className="inline-flex min-h-[44px] items-center gap-1 text-sm font-semibold text-slate-600 hover:underline">Official results <ExternalLink className="h-3.5 w-3.5" /></a>}
      </div>
      <DrawPageAlert stream={s} latestCutoff={latestScored?.cutoff ?? null} />
    </div>
  );
}
